import {Component, OnInit} from '@angular/core';
import {PageableResult} from '../../scroll-load-component/scroll-load.component';
import {UtilService} from '../../../services/util/util.service';
import {ViewService} from '../../../services/view/view.service';
import {Fee, FeePeriodPrice, SalespointWithAlias} from '../../../generated/sales-terminal-client';
import {FeeService, ServiceFeeFilter} from '../../../services/fee/fee.service';
import {finalize} from 'rxjs/operators';
import {ReplaySubject} from 'rxjs';
import {SalespointsService} from '../../../services/salespoints/salespoints.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ServiceFeeDialogComponent} from '../service-fee-dialog/service-fee-dialog.component';
import {find} from 'lodash-es';

export interface SortedFee extends Fee {
  currentPeriodPrice?: FeePeriodPrice;
  pastPeriodPrices?: FeePeriodPrice[];
  futurePeriodPrices?: FeePeriodPrice[];
  currentAndFuturePeriodPrices?: FeePeriodPrice[];
}

@Component({
  selector: 'app-service-fees',
  templateUrl: './service-fees.component.html',
  styleUrls: ['./service-fees.component.scss']
})
export class ServiceFeesComponent implements OnInit {
  public isLoading: boolean;
  public serviceFees: PageableResult<SortedFee>;
  public expanded: Record<number, boolean> = {};
  public filters: ServiceFeeFilter = {
    page: 1,
    pageSize: 30,
    showInactive: false,
    salespointIds: [],
    name: null
  };
  public showMobileFilters: boolean;
  public salespoints: SalespointWithAlias[] = [];
  public salespointsFiltered: ReplaySubject<SalespointWithAlias[]> = new ReplaySubject<SalespointWithAlias[]>();
  public activeTab: number = 0;
  public expandableCountActive: number;
  public expandableCountPast: number;

  constructor(public utilService: UtilService,
              public viewService: ViewService,
              private dialog: MatDialog,
              private feeService: FeeService,
              private salespointsService: SalespointsService) {
  }

  public get expandableFeesExist(): boolean {
    return this.activeTab === 0 && !!this.expandableCountActive || this.activeTab === 1 && !!this.expandableCountPast;
  }

  public ngOnInit(): void {
    this.getSalespoints();
  }

  private getSalespoints(): void {
    this.isLoading = true;
    this.salespointsService.getSalesPoints({ includeAliasSalespoints: true }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((salespoints) => {
      this.salespoints = salespoints;
      this.search(); // Initial search
    });
  }

  public search(page: number = 1): void {
    this.isLoading = true;
    this.filters.page = page;
    this.feeService.getServiceFees(this.filters).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((results: PageableResult<SortedFee>) => {
      this.expanded = {};
      this.serviceFees = results;
      this.serviceFees.items = this.serviceFees.items.map((fee: SortedFee) => this.feeService.sortServiceFeePeriods(fee));
      this.expandableCountActive = this.serviceFees.items.filter((fee: SortedFee) => fee.currentAndFuturePeriodPrices?.length > 1).length;
      this.expandableCountPast = this.serviceFees.items.filter((fee: SortedFee) => fee.pastPeriodPrices?.length > 1).length;
    });
  }

  public expandAll(): void {
    const data: Record<number, boolean> = {};
    this.serviceFees.items.forEach((fee) => {
      const expandablePeriodPrices = this.activeTab === 0 ? fee.currentAndFuturePeriodPrices : fee.pastPeriodPrices;
      if (expandablePeriodPrices?.length > 1) {
        data[fee.id] = true;
      }
    });
    this.expanded = data;
  }
  public collapseAll(): void {
    this.expanded = {};
  }

  public get areAllExpanded(): boolean {
    return Object.values(this.expanded).length === (this.activeTab === 0 ? this.expandableCountActive : this.expandableCountPast) && Object.values(this.expanded).every(expanded => !!expanded);
  }

  public createServiceFee(): void {
    const dialogRef: MatDialogRef<ServiceFeeDialogComponent> = this.dialog.open(ServiceFeeDialogComponent, {
      width: '60%'
    });
    dialogRef.afterClosed().subscribe(_ => this.search());
  }
  public editServiceFee(fee: Fee): void {
    const dialogRef: MatDialogRef<ServiceFeeDialogComponent> = this.dialog.open(ServiceFeeDialogComponent, {
      width: '60%',
      data: fee
    });
    dialogRef.afterClosed().subscribe(_ => this.search());
  }

  public changeTab(): void {
    switch (this.activeTab) {
      case 0:
        this.filters.showInactive = false;
        break;
      case 1:
        this.filters.showInactive = true;
        break;
    }
    this.search(1);
  }

  public toggleFee(feeId: number): void {
    this.expanded[feeId] = !this.expanded[feeId];
  }

  public clearFilters(): void {
    this.filters.salespointIds = [];
    this.filters.name = null;
  }

  public displaySalespointsByIds(idList: number[]): string {
    let resultString: string = '';
    this.salespoints.forEach(salespoint => {
      if (idList.includes(salespoint.id)) {
        resultString += salespoint.name + '; ';
      }
    });
    return resultString.length ? resultString.trim().replace(/,+$/, '') : '-';
  }
  public nameByIdDisplay(inId: number, inList: any[]): string {
    return find(inList, {id: inId})?.name;
  }
}
